import {Button, IconButton} from '@wandb/components';
// @todo: Migrate to 'Switch' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Switch} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import _ from 'lodash';
import * as React from 'react';
import {useCallback, useLayoutEffect, useMemo, useRef} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popup} from 'semantic-ui-react';

import * as Filter from '../../../util/filters';
import type * as FilterTypes from '../../../util/filterTypes';
import {
  activeFilterCountFromStored,
  type FilterV2Root,
  getFiltersForUI,
  isFilterV2,
  isFilterV2Group,
  type StoredFilter,
  upsertToggleFilter,
} from '../../../util/filterV2';
import {useRampFlagAndOrFilters} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import type * as RunTypes from '../../../util/runTypes';
import {maybePluralize} from '../../../util/uihelpers';
import {useTrackFilterWithLocation} from '../../Filters/filterEvents';
import {type FilterKeySelectorCreatorProps} from '../../Filters/FilterKeySelector';
import FilterList from '../../Filters/FilterList';
import FilterListV2 from '../../Filters/FilterListV2';
import {type FilterValueSelectorCreatorProps} from '../../Filters/FilterValueSelector';
import {useRunSelectorAnalyticsLocation} from '../RunSelectorAnalyticsLocationContext';

function filterCompare(
  v: FilterTypes.Filter<RunTypes.Key>,
  e: FilterTypes.Filter<RunTypes.Key>
) {
  return _.isEqual(_.omit(v, ['disabled']), _.omit(e, ['disabled']));
}

// Finds the index of the individual filter in the group filter
// NOTE: 1 depth of children only.
const filterIndex = (
  filters: FilterTypes.GroupFilter<RunTypes.Key>,
  checkFilter: FilterTypes.IndividualFilter<RunTypes.Key>
) => {
  if (filters.filters) {
    const index = _.findIndex(filters.filters, filter =>
      _.isEqualWith(filter, checkFilter, filterCompare)
    );
    return index;
  } else {
    return -1;
  }
};

// Checks if a group filter has a direct child that
// matches the individual filter that is enabled.
const hasEnabledFilter = (
  filters: FilterTypes.GroupFilter<RunTypes.Key>,
  checkFilter: FilterTypes.IndividualFilter<RunTypes.Key>
) => {
  const index = filterIndex(filters, checkFilter);
  if (index === -1) {
    return false;
  }

  return !filters.filters[index].disabled;
};

interface WBTableActionFilterButtonProps {
  className?: string;
  compact?: boolean;
  notificationOpen?: boolean;
  filtersOpen: boolean;
  filters: StoredFilter;
  onClick?(): void;
}

export const WBTableActionFilterButton = ({
  compact,
  filters,
  filtersOpen,
  notificationOpen,
  onClick,
  className,
}: WBTableActionFilterButtonProps) => {
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const filterCount = activeFilterCountFromStored(filters);
  const buttonText =
    filterCount > 0 ? maybePluralize(filterCount, 'filter') : 'Filter';

  const isActive = filtersOpen || filterCount > 0;
  const onPress = () => {
    onClick?.();
    window.analytics?.track('Filter dropdown clicked', {
      location: analyticsLocation,
    });
  };

  return (
    <Popup
      open={notificationOpen}
      position="bottom left"
      basic
      pinned
      style={{
        top: 24,
        marginLeft: 1,
        padding: '4px 8px',
        cursor: 'pointer',
      }}
      inverted
      on="click"
      content="New filter applied"
      trigger={
        compact ? (
          <IconButton
            tooltip={{children: 'Filter'}}
            aria-label="Filter"
            isActive={isActive}
            variant="secondary"
            icon="filter-alt"
            data-test="filter-button"
            onPress={onPress}
          />
        ) : (
          <Button
            isActive={isActive}
            variant="secondary"
            startIcon="filter-alt"
            data-test="filter-button"
            aria-label={buttonText}
            onPress={onPress}>
            {buttonText}
          </Button>
        )
      }
    />
  );
};

interface WBTableActionFilterPickerProps {
  filters: StoredFilter;

  defaultToggleFilters?: FilterTypes.DefaultToggleFilter[];

  overflowFunc?: () => void;

  setFilters(filters: StoredFilter): void;

  filterKeySelector(props: FilterKeySelectorCreatorProps): React.ReactNode;

  filterValueSelector(
    props: FilterValueSelectorCreatorProps<RunTypes.Key>
  ): React.ReactNode;
}

export const WBTableActionFilterPicker = (
  props: WBTableActionFilterPickerProps
) => {
  const {filters, overflowFunc} = props;
  const andOrEnabled = useRampFlagAndOrFilters();

  // Filter dropdown is a little jank in the custom charts editor.
  // We need to remount the dropdown when it juts out of the viewport,
  // so that we bring it back into view.
  const filterRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const raf = requestAnimationFrame(() => {
      if (filterRef.current == null || overflowFunc == null) {
        return;
      }
      const {top, left, right, bottom} =
        filterRef.current.getBoundingClientRect();
      const {innerWidth: viewportWidth, innerHeight: viewportHeight} = window;
      if (
        right > viewportWidth ||
        bottom > viewportHeight ||
        left < 0 ||
        top < 0
      ) {
        overflowFunc();
      }
    });
    return () => cancelAnimationFrame(raf);
  });

  return (
    <div className="filter-popup" ref={filterRef}>
      {andOrEnabled && isFilterV2(filters) ? (
        <FilterPickerV2Content {...props} />
      ) : (
        <FilterPickerLegacyContent {...props} />
      )}
    </div>
  );
};

const FilterPickerV2Content = (props: WBTableActionFilterPickerProps) => {
  const {filters, defaultToggleFilters} = props;
  const {trackGroupCreated, trackORToggled} = useTrackFilterWithLocation();
  const v2Root = useMemo(() => getFiltersForUI(filters), [filters]);

  const handleChange = useCallback(
    (newRoot: FilterV2Root) => {
      props.setFilters(newRoot);
    },
    [props]
  );

  const handleToggle = useCallback(
    (toggleFilter: FilterTypes.IndividualFilter<RunTypes.Key>) => {
      const updated = upsertToggleFilter(v2Root, toggleFilter);
      props.setFilters(updated);
    },
    [v2Root, props]
  );

  const hasEnabledToggle = useCallback(
    (toggleFilter: FilterTypes.IndividualFilter<RunTypes.Key>) =>
      v2Root.filters.some(
        item =>
          !isFilterV2Group(item) &&
          !item.disabled &&
          item.key.section === toggleFilter.key.section &&
          item.key.name === toggleFilter.key.name &&
          item.op === toggleFilter.op &&
          JSON.stringify(item.value) === JSON.stringify(toggleFilter.value)
      ),
    [v2Root]
  );

  return (
    <>
      <FilterListV2
        root={v2Root}
        onChange={handleChange}
        onGroupCreated={trackGroupCreated}
        onORToggled={trackORToggled}
        filterKeySelector={props.filterKeySelector}
        filterValueSelector={
          props.filterValueSelector as unknown as (
            p: FilterValueSelectorCreatorProps<FilterTypes.FilterKey>
          ) => React.ReactNode
        }
      />
      {defaultToggleFilters && (
        <WBTableActionFilterPickerDefaultToggles
          defaultToggleFilters={defaultToggleFilters}
          onToggle={handleToggle}
          hasEnabled={hasEnabledToggle}
          showDivider
        />
      )}
    </>
  );
};

const FilterPickerLegacyContent = (props: WBTableActionFilterPickerProps) => {
  const {filters, defaultToggleFilters} = props;
  const legacyFilters = isFilterV2(filters) ? Filter.EMPTY_FILTERS : filters;

  return (
    <>
      {legacyFilters.filters.map((filter, i) => (
        <FilterList
          key={i}
          filters={filter}
          canAdd={true}
          index={i}
          pushFilter={(newFilter: FilterTypes.Filter<RunTypes.Key>) => {
            props.setFilters(
              Filter.Update.groupPush(legacyFilters, [i], newFilter)
            );
          }}
          deleteFilter={(index: number) =>
            props.setFilters(
              Filter.Update.groupRemove(legacyFilters, [i], index)
            )
          }
          setFilter={(
            index: number,
            f: FilterTypes.IndividualFilter<RunTypes.Key>
          ) => {
            // must disable filter if it's invalid, otherwise it could crash the app
            const disabled = f.disabled ? f.disabled : !Filter.isValidFilter(f);
            props.setFilters(
              Filter.Update.setFilter(legacyFilters, [i, index], {
                ...f,
                disabled,
              })
            );
          }}
          filterKeySelector={props.filterKeySelector}
          filterValueSelector={props.filterValueSelector}
        />
      ))}
      {defaultToggleFilters && (
        <WBTableActionFilterPickerDefaultToggles
          defaultToggleFilters={defaultToggleFilters}
          onToggle={tf => {
            const toggleFilter =
              tf as FilterTypes.IndividualFilter<RunTypes.Key>;
            const index = filterIndex(legacyFilters.filters[0], toggleFilter);
            const filter = legacyFilters.filters[0].filters[index];

            if (index === -1) {
              props.setFilters(
                Filter.Update.groupPush(legacyFilters, [0], toggleFilter)
              );
            } else {
              props.setFilters(
                Filter.Update.setFilter(legacyFilters, [0, index], {
                  ...toggleFilter,
                  disabled: !filter.disabled,
                } as FilterTypes.IndividualFilter<RunTypes.Key>)
              );
            }
          }}
          hasEnabled={tf =>
            hasEnabledFilter(
              legacyFilters.filters[0],
              tf as FilterTypes.IndividualFilter<RunTypes.Key>
            )
          }
        />
      )}
    </>
  );
};

const WBTableActionFilterPickerDefaultToggles = ({
  defaultToggleFilters,
  onToggle,
  hasEnabled,
  showDivider,
}: {
  defaultToggleFilters: FilterTypes.DefaultToggleFilter[];
  onToggle: (filter: FilterTypes.IndividualFilter<RunTypes.Key>) => void;
  hasEnabled: (filter: FilterTypes.IndividualFilter<RunTypes.Key>) => boolean;
  showDivider?: boolean;
}) => (
  <Tailwind>
    {showDivider && <hr className="my-16 text-moon-250" />}
    <div className="flex flex-col gap-8">
      {defaultToggleFilters.map((defaultFilter, i) => (
        <div key={i} className="flex justify-between">
          <span>{defaultFilter.label}</span>
          <Switch.Root
            size="medium"
            checked={hasEnabled(defaultFilter.filter)}
            onCheckedChange={() => onToggle(defaultFilter.filter)}>
            <Switch.Thumb
              size="medium"
              checked={hasEnabled(defaultFilter.filter)}
            />
          </Switch.Root>
        </div>
      ))}
    </div>
  </Tailwind>
);

interface WBTableActionFilterProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  trigger: (isOpen: boolean) => React.ReactNode;
  content: React.ReactNode;
  preventOverflow?: boolean;
}

export const WBTableActionFilter = (props: WBTableActionFilterProps) => {
  return (
    <Popup
      basic
      className="wb-table-action-popup"
      style={{
        maxWidth: 'none',
      }}
      on="click"
      position="bottom left"
      open={props.open}
      onOpen={() => props.setOpen(true)}
      onClose={() => props.setOpen(false)}
      trigger={props.trigger(props.open)}
      content={
        <Tailwind>
          <div className="flex justify-between mb-10">
            <span className="text-lg font-semibold">Filters</span>
            <IconButton
              size="md"
              variant="ghost"
              icon="close"
              aria-label="Close filters"
              onPress={() => props.setOpen(false)}
            />
          </div>
          {props.content}
        </Tailwind>
      }
      popperModifiers={{
        preventOverflow: {enabled: props.preventOverflow ?? false},
        flip: {enabled: false},
      }}
    />
  );
};
